/**
 * WebSocketEventLogger - Component for logging and displaying WebSocket events in real-time
 *
 * This component captures all WebSocket messages (sent and received) and displays them
 * in a copy-pasteable format for debugging purposes.
 */

import {
  Activity,
  AlertCircle,
  ArrowDown,
  ArrowUp,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Copy,
  Trash2,
} from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";

export interface WebSocketEvent {
  id: string;
  timestamp: number;
  direction: "sent" | "received";
  type: string;
  data: unknown;
  error?: string;
}

interface WebSocketEventLoggerProps {
  events: WebSocketEvent[];
  onClearEvents: () => void;
}

const formatTimestamp = (timestamp: number) => {
  return new Date(timestamp).toLocaleTimeString("en-US", {
    hour12: false,
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
    fractionalSecondDigits: 3,
  });
};

const getEventTypeColor = (type: string, direction: "sent" | "received") => {
  if (direction === "sent") {
    return "bg-blue-100 text-blue-800";
  }

  // Color code received events by type
  switch (type) {
    case "PLAYBOOK_PROGRESS":
    case "PLAYBOOK_SECTION_UPDATE":
      return "bg-green-100 text-green-800";
    case "ERROR":
      return "bg-red-100 text-red-800";
    case "AI_STREAM_START":
    case "AI_STREAM_CHUNK":
    case "AI_STREAM_END":
      return "bg-purple-100 text-purple-800";
    case "TRANSCRIPT_UPDATE":
      return "bg-yellow-100 text-yellow-800";
    default:
      return "bg-gray-100 text-gray-800";
  }
};

const DirectionIcon = ({ direction }: { direction: "sent" | "received" }) => {
  return direction === "sent" ? (
    <ArrowUp className="h-4 w-4 text-blue-600" />
  ) : (
    <ArrowDown className="h-4 w-4 text-green-600" />
  );
};

const EventStatusIcon = ({ event }: { event: WebSocketEvent }) => {
  if (event.error) {
    return <AlertCircle className="h-4 w-4 text-red-600" />;
  }
  return <CheckCircle className="h-4 w-4 text-green-600" />;
};

export function WebSocketEventLogger({
  events,
  onClearEvents,
}: Readonly<WebSocketEventLoggerProps>) {
  const [expandedEvents, setExpandedEvents] = useState<Set<string>>(new Set());
  const [filter, setFilter] = useState<"all" | "sent" | "received" | "errors">(
    "all",
  );
  const scrollRef = useRef<HTMLDivElement>(null);

  const toggleEventExpansion = (eventId: string) => {
    const newExpanded = new Set(expandedEvents);
    if (newExpanded.has(eventId)) {
      newExpanded.delete(eventId);
    } else {
      newExpanded.add(eventId);
    }
    setExpandedEvents(newExpanded);
  };

  const copyEventToClipboard = useCallback((event: WebSocketEvent) => {
    const eventText = `=== WebSocket Event ===
Timestamp: ${formatTimestamp(event.timestamp)}
Direction: ${event.direction.toUpperCase()}
Type: ${event.type}
${event.error ? `Error: ${event.error}\n` : ""}
Data:
${JSON.stringify(event.data, null, 2)}
=====================`;

    navigator.clipboard
      .writeText(eventText)
      .then(() => {
        console.log("Event copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy event:", err);
      });
  }, []);

  const filteredEvents = events.filter((event) => {
    switch (filter) {
      case "sent":
        return event.direction === "sent";
      case "received":
        return event.direction === "received";
      case "errors":
        return !!event.error;
      default:
        return true;
    }
  });

  const copyAllEventsToClipboard = useCallback(() => {
    const allEventsText = filteredEvents
      .map((event) => {
        return `=== WebSocket Event ===
Timestamp: ${formatTimestamp(event.timestamp)}
Direction: ${event.direction.toUpperCase()}
Type: ${event.type}
${event.error ? `Error: ${event.error}\n` : ""}
Data:
${JSON.stringify(event.data, null, 2)}
=====================`;
      })
      .join("\n\n");

    navigator.clipboard
      .writeText(allEventsText)
      .then(() => {
        console.log("All events copied to clipboard");
      })
      .catch((err) => {
        console.error("Failed to copy all events:", err);
      });
  }, [filteredEvents]);

  // Auto-scroll to bottom when new events arrive
  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
    }
  }, []);

  return (
    <div className="bg-white border border-gray-300 rounded-lg text-sm">
      {/* Header */}
      <div className="flex items-center justify-between p-3 border-b border-gray-300 bg-gray-50">
        <div className="flex items-center gap-2">
          <Activity className="h-4 w-4" />
          <span className="font-medium">WebSocket Events</span>
          <Badge variant="outline">{filteredEvents.length} events</Badge>
        </div>

        <div className="flex items-center gap-2">
          {/* Filter buttons */}
          <div className="flex bg-white border border-gray-200 rounded-md">
            {(["all", "sent", "received", "errors"] as const).map(
              (filterOption) => (
                <button
                  key={filterOption}
                  type="button"
                  onClick={() => setFilter(filterOption)}
                  className={`px-2 py-1 text-xs font-medium capitalize transition-colors ${
                    filter === filterOption
                      ? "bg-blue-100 text-blue-700"
                      : "text-gray-600 hover:text-gray-900"
                  } ${filterOption === "all" ? "rounded-l" : ""} ${filterOption === "errors" ? "rounded-r" : ""}`}
                >
                  {filterOption}
                </button>
              ),
            )}
          </div>

          <Button
            size="sm"
            variant="outline"
            onClick={copyAllEventsToClipboard}
            disabled={filteredEvents.length === 0}
          >
            <Copy className="h-3 w-3 mr-1" />
            Copy All
          </Button>

          <Button
            size="sm"
            variant="outline"
            onClick={onClearEvents}
            disabled={events.length === 0}
          >
            <Trash2 className="h-3 w-3 mr-1" />
            Clear
          </Button>
        </div>
      </div>

      {/* Events Content */}
      <div ref={scrollRef} className="max-h-96 overflow-y-auto">
        {filteredEvents.length === 0 ? (
          <div className="p-4 text-gray-500 text-center">
            {filter === "all"
              ? "No WebSocket events yet. Start a call to see activity..."
              : `No ${filter} events found.`}
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {filteredEvents
              .slice()
              .reverse() // Show newest first
              .map((event) => {
                const isExpanded = expandedEvents.has(event.id);

                return (
                  <div key={event.id} className="p-3">
                    {/* Event Header */}
                    <div className="flex items-start gap-2">
                      <button
                        className="flex items-start gap-2 flex-1 text-left hover:bg-gray-50 p-2 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                        onClick={() => toggleEventExpansion(event.id)}
                        type="button"
                      >
                        <DirectionIcon direction={event.direction} />
                        <EventStatusIcon event={event} />

                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-gray-500 font-mono text-xs">
                              {formatTimestamp(event.timestamp)}
                            </span>
                            <Badge
                              className={`text-xs font-medium ${getEventTypeColor(event.type, event.direction)}`}
                              variant="outline"
                            >
                              {event.type}
                            </Badge>
                            <span
                              className={`text-xs px-2 py-0.5 rounded ${
                                event.direction === "sent"
                                  ? "bg-blue-50 text-blue-700"
                                  : "bg-green-50 text-green-700"
                              }`}
                            >
                              {event.direction.toUpperCase()}
                            </span>
                          </div>

                          {event.error && (
                            <div className="text-red-600 text-xs mb-1">
                              Error: {event.error}
                            </div>
                          )}

                          <div className="text-gray-600 text-xs truncate">
                            {JSON.stringify(event.data).substring(0, 100)}...
                          </div>
                        </div>

                        {isExpanded ? (
                          <ChevronUp className="h-3 w-3 text-gray-400" />
                        ) : (
                          <ChevronDown className="h-3 w-3 text-gray-400" />
                        )}
                      </button>

                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => copyEventToClipboard(event)}
                        className="p-1 h-6 w-6"
                        type="button"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>

                    {/* Expanded Details */}
                    {isExpanded && (
                      <div className="mt-3 ml-8 space-y-3">
                        <div>
                          <div className="text-gray-700 font-medium mb-1 text-xs">
                            Full Event Data:
                          </div>
                          <pre className="bg-gray-100 p-3 rounded border text-xs overflow-x-auto text-gray-900 font-mono">
                            {JSON.stringify(event.data, null, 2)}
                          </pre>
                        </div>

                        {event.error && (
                          <div>
                            <div className="text-gray-700 font-medium mb-1 text-xs">
                              Error Details:
                            </div>
                            <div className="bg-red-50 p-2 rounded border text-xs text-red-800 font-mono">
                              {event.error}
                            </div>
                          </div>
                        )}

                        <div className="text-gray-500 text-xs font-mono">
                          Event ID: {event.id}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })}
          </div>
        )}
      </div>
    </div>
  );
}
